import type { CSSProperties } from 'react'
import { Header } from 'semantic-ui-react'
import ImgTemplate from '../../../../utilites/ImgTemplate'

const paragraphStyle = {
  marginBottom: '1em',
  textAlign: 'justify',
} satisfies CSSProperties

const imageContainerStyle: CSSProperties = {
  textAlign: 'center',
  marginBottom: '1.2em',
}

/** Wrappery ograniczające rozmiar obrazu */
const imageWrapperDefault: CSSProperties = {
  maxWidth: '1100px',
  marginInline: 'auto',
}

export default function ROSIERadioTelescope() {
  return (
    <div className='fade-in'>
      <Header as='h1' style={{ textAlign: 'center' }}>
        ROSIE Radio Telescope
      </Header>

      {/* Intro / Location */}
      <div style={paragraphStyle}>
        The ROSIE Radio Telescope is a modern research facility installed at the Białków Astronomical Observatory and
        operated by the Institute of Astronomy, University of Wrocław (Poland). Located at N51°.4730, E16°.6589, ROSIE
        supports advanced solar patrol and selected astrophysical radio observations within the 1–4 GHz band. The system
        combines high sensitivity, fine time and spectral resolution, and real-time data access to serve both routine
        monitoring and targeted campaigns.
      </div>
      <div style={imageContainerStyle}>
        <div style={imageWrapperDefault}>
          <ImgTemplate
            srcName='observatory/RT6_pavilion_OEA_from_SE.webp'
            alt='ROSIE Radio Telescope overview'
            width='100%'
          />
          <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
            Source: P. Rudawy, IA UWr
          </p>
        </div>
      </div>

      {/* Antenna System */}
      <Header as='h2' style={{ textAlign: 'center' }}>
        Antenna System
      </Header>
      <div style={paragraphStyle}>
        ROSIE employs a 6 m parabolic antenna with a 1.7 m focal length, optimized for high-precision microwave
        observations. Solid aluminum reflector panels are mounted to a lightweight steel truss supported by a central
        truncated cone. A 3D laser scan confirmed an RMS surface deviation of 1.4 mm from the ideal parabola. This
        surface quality yields efficiencies above 94% at 4 GHz, enabling excellent performance for solar patrol and
        astrophysical targets alike.
      </div>
      <div style={imageContainerStyle}>
        <div style={{ maxWidth: '525px', width: '100%', marginInline: 'auto' }}>
          <ImgTemplate srcName='observatory/radiotelescope_front_view.webp' alt='6 m parabolic antenna' width='100%' />
          <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
            Source: P. Rudawy, IA UWr
          </p>
        </div>
      </div>

      {/* Mount and Drives */}
      <Header as='h2' style={{ textAlign: 'center' }}>
        Mount and Drive Mechanisms
      </Header>
      <div style={paragraphStyle}>
        The antenna operates on a computer-controlled alt-azimuth mount offering full hemispherical coverage (90°
        elevation, 540° azimuth). The elevation axis is located 2.5 m above the top surface of a 3 m concrete
        foundation, placing the antenna axis 5.5 m above ground level at 0° elevation. Electric drives (APM-FEP30AMK2,
        three-phase, 3.8 kW) on both axes provide smooth and precise tracking with a pointing accuracy of 0.003° (RMS).
        Dual-motor azimuth control removes mechanical backlash, while the elevation axis uses a screw actuator balanced
        by the antenna’s own mass. A 540° azimuth slewing takes about 15 minutes and a 90° elevation move about 6
        minutes. High-resolution encoders with redundant limit switches ensure dependable feedback and operational
        safety. Automatic routines continuously monitor wind; when gusts exceed 15 m/s the antenna is stowed to 90°
        elevation.
      </div>
      <div style={imageContainerStyle}>
        <div style={imageWrapperDefault}>
          <ImgTemplate
            srcName='observatory/radiotelescope_view_3.webp'
            alt='Alt-az mount and drive mechanisms'
            width='100%'
          />
          <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
            Source: P. Rudawy, IA UWr
          </p>
        </div>
      </div>

      {/* Receiver & Front-End */}
      <Header as='h2' style={{ textAlign: 'center' }}>
        Receiver and Front-End Electronics
      </Header>
      <div style={paragraphStyle}>
        A dual-polarization receiver is mounted at the focus on a stable three-leg feed support. The front-end module
        integrates a low-noise amplifier (LNA), a broadband noise calibration source (BBGen+, 20–6000 MHz), a
        directional coupler, a coaxial switch, and a preamplifier in a sealed, temperature-stabilized enclosure. To
        prevent condensation, the front-end is continuously purged with dry pressurized air supplied by an oil-free
        compressor and an Omi HL S012 dryer. A climate-controlled observation cabin north of the antenna houses power
        distribution, a 10 kWh three-phase UPS, and high-speed networking. A 21 m lightning-protection mast provides
        coverage meeting at least Protection Class II for the entire installation.
      </div>
      <div style={imageContainerStyle}>
        <div style={{ maxWidth: '525px', width: '100%', marginInline: 'auto' }}>
          <ImgTemplate srcName='observatory/tripod_front_end.webp' alt='Front-end enclosure and feed' width='100%' />
          <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
            Source: B. Kaźmierczak, ITTI
          </p>
        </div>
      </div>

      {/* Receiver & Digitization */}
      <Header as='h2' style={{ textAlign: 'center' }}>
        Receiver and Digitization System
      </Header>
      <div style={paragraphStyle}>
        The IF chain is digitized by an Ettus X310 software-defined receiver controlled by dedicated software on a local
        server. A fully redundant backup receiver runs in parallel for continuous operation. ROSIE records
        simultaneously in two bands — 10.7 cm (1.0 GHz) and 30 cm (2.8 GHz) — each with 100 MHz bandwidth, with
        alternative sub-bands selectable anywhere within 1–4 GHz. The system supports 200 Msps sampling with 12-bit dual
        dynamic-range conversion and an effective dynamic range above 100 dB. Flux measurements cover from 1 SFU up to
        at least 100 000 SFU. Up to 1000 spectra per second per channel are produced, each with 8192 points. In addition
        to spectra, integrated flux is computed in real time. Absolute calibration uses established standards (Cas A,
        Cyg A, Vir A, Tau A), while solar data reference a precision noise diode.
      </div>
      <div style={imageContainerStyle}>
        <div style={{ maxWidth: '525px', width: '100%', marginInline: 'auto' }}>
          <ImgTemplate srcName='observatory/front_end_scheme.webp' alt='Digitization and backend' width='100%' />
          <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
            Source: E. Pazderski, UMK
          </p>
        </div>
      </div>

      {/* Control & Data */}
      <Header as='h2' style={{ textAlign: 'center' }}>
        Control and Data System
      </Header>
      <div style={paragraphStyle}>
        The control system supports multiple modes of operation: automated observing via scheduled scripts generated by
        dedicated tools, local manual control under operator supervision, and secure remote operation over the Internet.
        A graphical user interface enables intuitive management - from antenna steering and schedule creation to
        real-time monitoring and visualization. Observatory-wide time and frequency references (10 MHz and 1 s) are
        distributed from a GPS-disciplined atomic clock. All raw and calibrated data are available instantly for
        partners, facilitating collaborative science and long-term monitoring programs.
      </div>

      {/* Summary */}
      <Header as='h2' style={{ textAlign: 'center' }}>
        Summary
      </Header>
      <div style={paragraphStyle}>
        With a precision 6 m antenna, dual-band receiver, and fully digital control and acquisition, the ROSIE Radio
        Telescope delivers a capable, cost-effective platform for solar and astrophysical radio studies. It merges
        engineering precision with operational flexibility and open data access, supporting the international research
        community through dependable performance and rapid availability of results.
      </div>
    </div>
  )
}
